import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

import { SOURCE_PIPELINE_OPTIONS, SourcePipeline } from '@core/api/core';

import { LMRFolderItem } from '../../models';
import { LMRSourcesService } from '../../services/lmr-sources.service';
import { isPdfFile } from '@shared/utils/isPdfFile';

export interface ValidaitorUploadDestinationDialogData {
  files: File[];
}

export interface ValidaitorUploadDestinationDialogResult {
  folderId: string;
  uploadedCount: number;
}

@Component({
  selector: 'app-validaitor-upload-destination-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatButton, NgSelectModule],
  templateUrl: './validaitor-upload-destination-dialog.component.html',
  styleUrl: './validaitor-upload-destination-dialog.component.scss',
})
export class ValidaitorUploadDestinationDialogComponent {
  private readonly _dialogRef = inject(
    MatDialogRef<ValidaitorUploadDestinationDialogComponent, ValidaitorUploadDestinationDialogResult>
  );
  private readonly _data = inject<ValidaitorUploadDestinationDialogData>(MAT_DIALOG_DATA);
  private readonly _sourcesService = inject(LMRSourcesService);

  protected readonly pipelineOptions = SOURCE_PIPELINE_OPTIONS;

  protected readonly files = signal<File[]>(this._data.files);
  protected readonly folders = signal<LMRFolderItem[]>([]);
  protected readonly pipeline = signal<SourcePipeline>('vision');
  protected readonly hasPdfFiles = computed(() => this.files().some(file => isPdfFile(file)));
  protected readonly foldersLoading = signal(true);
  protected readonly selectedFolderId = signal<string | null>(null);
  protected readonly isUploading = signal(false);
  protected readonly uploadedCount = signal(0);
  protected readonly failedFiles = signal<File[]>([]);

  protected readonly selectedFolderTitle = computed(() => {
    const folderId = this.selectedFolderId();
    return this.folders().find(folder => folder._id === folderId)?.title ?? null;
  });

  protected readonly canUpload = computed(
    () =>
      this.folders().length > 0 &&
      !!this.selectedFolderId() &&
      this.files().length > 0 &&
      !this.isUploading()
  );

  protected readonly uploadButtonLabel = computed(() => {
    if (this.isUploading()) {
      return 'Uploading…';
    }

    const folderTitle = this.selectedFolderTitle();
    const fileLabel = this.files().length === 1 ? 'file' : 'files';
    if (folderTitle) {
      return `Upload ${this.files().length} ${fileLabel} to ${folderTitle}`;
    }

    return `Upload ${this.files().length} ${fileLabel}`;
  });

  constructor() {
    this._loadFolders();
  }

  protected onFolderChange(folderId: string | null): void {
    if (!folderId || !this.folders().some(folder => folder._id === folderId)) {
      this.selectedFolderId.set(null);
      return;
    }

    this.selectedFolderId.set(folderId);
  }

  protected close(): void {
    this._dialogRef.close();
  }

  protected closeWithUploadedFiles(): void {
    const folderId = this.selectedFolderId();
    if (!folderId || this.uploadedCount() === 0) {
      return;
    }

    this._dialogRef.close({ folderId, uploadedCount: this.uploadedCount() });
  }

  protected async uploadFiles(files = this.files()): Promise<void> {
    const folderId = this.selectedFolderId();
    if (!folderId || this.isUploading()) {
      return;
    }

    this.isUploading.set(true);
    this.failedFiles.set([]);

    const failed: File[] = [];
    let uploaded = this.uploadedCount();

    for (const file of files) {
      try {
        await firstValueFrom(this._sourcesService.uploadSourceFile(folderId, file, 'validator', this.pipeline()));
        uploaded += 1;
        this.uploadedCount.set(uploaded);
      } catch {
        failed.push(file);
      }
    }

    this.failedFiles.set(failed);
    this.files.set(failed.length ? failed : []);
    this.isUploading.set(false);

    if (failed.length === 0) {
      this._dialogRef.close({ folderId, uploadedCount: uploaded });
    }
  }

  private _loadFolders(): void {
    this._sourcesService.loadLmrFolders('validator').subscribe({
      next: folders => {
        this.folders.set(folders);
        this.selectedFolderId.set(folders[0]?._id ?? null);
      },
      complete: () => this.foldersLoading.set(false),
      error: () => this.foldersLoading.set(false),
    });
  }
}
